import React from 'react';
import { Box, Typography, LinearProgress, Button, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { formatCount, TrialLimitNotice, trialUsage } from '../../modules/pricingJourney/components/TrialBanner';

interface OptimizationLimitWidgetProps {
  onLimitReached: (reached: boolean) => void; // Comunicación con el padre para bloquear el botón
}

const OptimizationLimitWidget: React.FC<OptimizationLimitWidgetProps> = ({ onLimitReached }) => {
  const { user, pricingAccess } = useAuth();
  // PRC-020: en prueba manda el límite de la prueba (1.000), no el mensual.
  const isTrial = pricingAccess?.state === 'trial';
  const trial = trialUsage(pricingAccess);

  // 1. Obtener datos con seguridad
  const usage = isTrial ? trial.used : (user?.usage_optimizations_this_month || 0);
  const limit = isTrial ? trial.limit : (user?.tier_details?.limit_optimizations_per_month || 10);
  
  // Si el límite es gigante (Growth/Autonomy), no mostramos nada o un mensaje sutil
  const isUnlimited = !isTrial && limit > 1000;

  // 2. Calcular estado
  const isLimitReached = usage >= limit;
  const percentage = Math.min((usage / limit) * 100, 100);

  // 3. Notificar al padre (Page) si debe bloquear
  React.useEffect(() => {
    onLimitReached(isLimitReached && !isUnlimited);
  }, [isLimitReached, isUnlimited, onLimitReached]);

  // CÁLCULO DE FECHA: Siempre el día 1 del mes siguiente
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const resetDate = nextMonth.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });

  if (isUnlimited) {
    // Para usuarios PRO/Enterprise, no mostramos límites, quizás un badge de "Ilimitado"
    return null; 
  }

  if (isTrial) {
    if (isLimitReached) return <TrialLimitNotice />;
    // PRC-046 (boceto G6): arriba del panel de Evaluación rápida, x de 1.000 con barra.
    return (
      <Box sx={{ border: '1px solid #1e293b', borderRadius: '12px', p: '12px 14px', bgcolor: '#0b1424', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#94a3b8' }}>Uso de la prueba</Box>
          <Box component="span" sx={{ fontSize: 13, color: '#94a3b8' }}>
            {formatCount(usage)} de {formatCount(limit)} optimizaciones
          </Box>
        </Box>
        <Box sx={{ height: 6, borderRadius: 999, bgcolor: '#1e293b', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${percentage}%`, bgcolor: '#38bdf8', borderRadius: 999 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: isLimitReached ? '#f87171' : '#1e293b', borderRadius: '12px', p: '12px 14px', bgcolor: '#0b1424' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold" color={isLimitReached ? 'error' : 'text.primary'}>
          Uso del Plan Discovery
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {usage} / {limit} consultas
        </Typography>
      </Box>

      <LinearProgress 
        variant="determinate" 
        value={percentage} 
        color={isLimitReached ? "error" : "primary"}
        sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Se reinicia {resetDate}
        </Typography>
        
        {isLimitReached && (
          <Button 
            component={Link} 
            to="/pricing" 
            size="small" 
            variant="outlined" 
            color="error"
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Aumentar Límites
          </Button>
        )}
      </Box>
      
      {isLimitReached && (
        <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
          Has agotado tus optimizaciones gratuitas de este mes.
        </Alert>
      )}
    </Box>
  );
};

export default OptimizationLimitWidget;